import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import EmailHtmlPreview from "@/components/EmailHtmlPreview";
import FilaCorreoBuzon from "@/components/buzon/FilaCorreoBuzon";
import { ApiError, api } from "@/lib/api";
import type {
  AdjuntoBuzon,
  CasoListItem,
  CuerpoBuzon,
  EstadoBuzon,
  FiltroBuzon,
  ListaBuzon,
  MensajeBuzon,
  Page,
  ResultadoJalar,
} from "@/lib/api";
import { fmtBytes, folioCorto } from "@/lib/formato";
import { IconoAdjunto, IconoAgregar, IconoBuscar, IconoIgnorar } from "@/ui/Iconos";
import VisorArchivo from "@/ui/VisorArchivo";
import { ayudaRevisarCorreo } from "@/components/ayuda/vistas/revisarCorreo";
import AyudaVista from "@/ui/AyudaVista";
import Modal from "@/ui/Modal";
import { ToggleTrack } from "@/ui/Toggle";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";

/**
 * «Revisar correo»: tu buzón, en solo lectura, dentro de ArkTickets.
 *
 * **No es tu buzón — es lo que ArkTickets cree que te falta capturar.** Por eso
 * abre en el filtro «Puede servir» y ver el buzón completo cuesta un clic
 * deliberado. Si abriera con los 4,000 correos por fecha, sería un cliente de
 * correo con otro nombre.
 *
 * Nada de lo que se ve aquí se guarda en la base. Solo al jalar o al ignorar se
 * escribe algo, y lo que se escribe es una decisión, no una copia del correo.
 *
 * Responsive: dos paneles desde **`lg` (1024)**, no desde `md`. Entre 768 y 1024
 * la app ya muestra el chrome de móvil junto con las rejillas de escritorio;
 * meter dos columnas de ~380 px ahí sería apilar una tercera regla sobre un
 * punto que ya está tenso. Debajo de `lg` son dos pantallas: lista → correo.
 */

const FILTROS: { id: FiltroBuzon; etiqueta: string; conteo: string }[] = [
  { id: "puede_servir", etiqueta: "Puede servir", conteo: "puede_servir" },
  { id: "todo", etiqueta: "Todo el buzón", conteo: "todo" },
  { id: "ignorados", etiqueta: "Ignorados", conteo: "ignorado" },
];

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Se llama tras jalar correos, para que quien abrió refresque lo suyo. */
  onJalado?: (r: ResultadoJalar) => void;
  /** Se jalaron correos SIN caso destino: quien abrió debe abrir el modal de
   * caso nuevo con el prefill y, ya creado el caso, volver a pedir `mensajes`
   * con ese caso — así sus archivos llegan a Evidencias. */
  onJaladoSinCaso?: (
    r: ResultadoJalar,
    mensajes: { uid: number; message_id: string | null }[],
  ) => void;
};

export default function RevisarCorreoModal({ open, onOpenChange, onJalado, onJaladoSinCaso }: Props) {
  const toast = useToast();
  const [cargando, setCargando] = useState(false);
  const [lista, setLista] = useState<ListaBuzon | null>(null);
  const [estado, setEstado] = useState<EstadoBuzon | null>(null);
  const [filtro, setFiltro] = useState<FiltroBuzon>("puede_servir");
  const [dias, setDias] = useState(30);
  const [q, setQ] = useState("");
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [abierto, setAbierto] = useState<MensajeBuzon | null>(null);
  const [cuerpo, setCuerpo] = useState<CuerpoBuzon | null>(null);
  const [cargandoCuerpo, setCargandoCuerpo] = useState(false);
  const [vistaCuerpo, setVistaCuerpo] = useState<"texto" | "html">("texto");
  const [trabajando, setTrabajando] = useState(false);
  // Selector de caso: se abre en el panel derecho, no en otro modal encima.
  const [eligiendoCaso, setEligiendoCaso] = useState(false);
  const [qCaso, setQCaso] = useState("");
  const [casos, setCasos] = useState<CasoListItem[]>([]);
  // Por default solo casos ABIERTOS. Pegarle un correo a un caso cerrado
  // revive una conversación que alguien dio por terminada, así que si de
  // verdad se quiere hay que pedirlo — no debe salir por descuido entre los
  // primeros resultados. Joe: «me trae hasta los cerrados».
  const [incluirCerrados, setIncluirCerrados] = useState(false);
  const debounceCaso = useRef<number | undefined>(undefined);
  const touchX = useRef<number | null>(null);
  // Visor de archivos del correo. Se abre ENCIMA del modal, con el mismo
  // componente que usan evidencias y guías: un archivo se ve igual venga de
  // donde venga.
  const [visor, setVisor] = useState<{ lista: AdjuntoBuzon[]; i: number } | null>(null);
  const debounce = useRef<number | undefined>(undefined);

  const cargar = useCallback(
    async (opts?: { filtro?: FiltroBuzon; dias?: number; q?: string }) => {
      setCargando(true);
      try {
        const f = opts?.filtro ?? filtro;
        const d = opts?.dias ?? dias;
        const busca = (opts?.q ?? q).trim();
        const url =
          `/api/buzon/mensajes?filtro=${f}&dias=${d}&tam=100` +
          (busca ? `&q=${encodeURIComponent(busca)}` : "");
        setLista(await api.get<ListaBuzon>(url));
      } catch (e) {
        const msg = e instanceof ApiError ? e.detail : "Error inesperado";
        toast.error("No pudimos entrar a tu correo", msg);
        setLista(null);
      } finally {
        setCargando(false);
      }
    },
    [filtro, dias, q, toast],
  );

  useEffect(() => {
    if (!open) return;
    setSeleccion(new Set());
    setAbierto(null);
    setCuerpo(null);
    void api.get<EstadoBuzon>("/api/buzon/estado").then(setEstado).catch(() => setEstado(null));
    void cargar();
    // Solo al abrir: los cambios de filtro/búsqueda tienen su propio camino.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cambiar de filtro LIMPIA la selección. Es el precedente de la casa
  // (`AsignarCasoModal`): la lista cambia bajo los pies y quedaría seleccionado
  // algo que ya no se ve.
  function cambiarFiltro(f: FiltroBuzon) {
    setFiltro(f);
    setSeleccion(new Set());
    setAbierto(null);
    void cargar({ filtro: f });
  }

  function escribirBusqueda(valor: string) {
    setQ(valor);
    setSeleccion(new Set());
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => void cargar({ q: valor }), 300);
  }

  async function abrirCorreo(m: MensajeBuzon) {
    // El visor lleva la lista de archivos del correo en que se abrió: si
    // sobrevive al cambio de correo, pide esos índices con el UID del nuevo
    // y enseña archivos que no son de este correo.
    setVisor(null);
    setAbierto(m);
    setCuerpo(null);
    setVistaCuerpo("texto");
    setCargandoCuerpo(true);
    try {
      const mid = m.message_id ? `?message_id=${encodeURIComponent(m.message_id)}` : "";
      const c = await api.get<CuerpoBuzon>(`/api/buzon/mensajes/${m.uid}/cuerpo${mid}`);
      setCuerpo(c);
      if (!c.existe) {
        toast.info(
          c.cambio ? "Tu correo cambió de orden" : "Ese correo ya no está en tu buzón",
          c.cambio ? "Volvemos a cargar la lista." : "Alguien lo movió o lo borró.",
        );
        void cargar();
      } else if (!c.texto && c.html) {
        setVistaCuerpo("html");
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : "Error inesperado";
      toast.error("No pudimos traer este correo", msg);
    } finally {
      setCargandoCuerpo(false);
    }
  }

  const mensajes = lista?.mensajes ?? [];

  /**
   * Sobre qué actúan los botones.
   *
   * Si hay selección, sobre ella. Si no hay pero tienes un correo ABIERTO,
   * sobre ese. Suena obvio y no lo era: el modal se diseñó pensando en
   * escritorio, donde ves la lista y el correo al mismo tiempo y seleccionas
   * mientras lees. En el celular el correo TAPA la lista, así que había que
   * salirse, marcar el switch y volver a entrar para poder hacer algo — lo
   * reportó Joe usándolo, y tenía toda la razón.
   */
  const objetivo = useMemo(() => {
    const marcados = mensajes.filter((m) => seleccion.has(m.uid));
    if (marcados.length) return marcados;
    return abierto ? [abierto] : [];
  }, [mensajes, seleccion, abierto]);

  const refs = useMemo(
    () => objetivo.map((m) => ({ uid: m.uid, message_id: m.message_id })),
    [objetivo],
  );

  /**
   * El caso al que apuntan TODOS los correos seleccionados, si es uno solo.
   *
   * Es lo que hace que el botón diga la verdad. Antes decía siempre «Abrir
   * caso» aunque el correo elegido ya fuera del CASO-2026-00080 —lo reportó
   * Joe usándolo— y ofrecer crear un caso nuevo para una respuesta que ya
   * tiene el suyo es justo el error que el veredicto estaba tratando de evitar.
   */
  const casoComun = useMemo(() => {
    if (!objetivo.length) return null;
    const ids = new Set(objetivo.map((m) => m.veredicto.caso_id));
    if (ids.size !== 1) return null;
    const primero = objetivo[0].veredicto;
    return primero.caso_id ? { id: primero.caso_id, folio: primero.caso_folio ?? "" } : null;
  }, [objetivo]);

  // Búsqueda de casos server-side con rebote, igual que AsignarCasoModal.
  useEffect(() => {
    if (!eligiendoCaso) return;
    window.clearTimeout(debounceCaso.current);
    const q = qCaso.trim();
    debounceCaso.current = window.setTimeout(
      () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (!incluirCerrados) params.set("abiertos", "true");
        params.set("page_size", "50");
        void api
          .get<Page<CasoListItem>>(`/api/casos?${params.toString()}`)
          .then((p) => setCasos(p.items))
          .catch(() => setCasos([]));
      },
      q ? 300 : 0,
    );
  }, [eligiendoCaso, qCaso, incluirCerrados]);

  /**
   * Pasar de un correo a otro sin salir del visor.
   *
   * Mismo gesto y mismo umbral (60 px) que el visor de evidencias
   * (`ui/VisorArchivo`): en esta app deslizar de lado ya significa «el
   * siguiente», y dos umbrales distintos para el mismo gesto se sienten como
   * dos apps. En escritorio, las flechas ← → hacen lo mismo.
   *
   * Se mueve dentro de la lista FILTRADA, que es la que el humano está viendo:
   * saltar a un correo que no está en pantalla sería teletransportarse.
   */
  const irA = useCallback(
    (paso: number) => {
      if (!abierto) return;
      const i = mensajes.findIndex((m) => m.uid === abierto.uid);
      const destino = mensajes[i + paso];
      if (destino) void abrirCorreo(destino);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [abierto, mensajes],
  );

  useEffect(() => {
    if (!open || !abierto) return;
    const h = (e: KeyboardEvent) => {
      // Con modificadores no es del carrusel: son atajos de otra cosa.
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key === "ArrowLeft") irA(-1);
      else if (e.key === "ArrowRight") irA(1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, abierto, irA]);

  function alternar(uid: number) {
    setSeleccion((prev) => {
      const s = new Set(prev);
      if (s.has(uid)) s.delete(uid);
      else s.add(uid);
      return s;
    });
  }

  async function jalar(casoId: number | null) {
    if (!refs.length) return;
    setTrabajando(true);
    try {
      const r = await api.post<ResultadoJalar>("/api/buzon/jalar", {
        uidvalidity: lista?.uidvalidity ?? null,
        mensajes: refs,
        caso_id: casoId,
      });
      const n = r.jalados.length;
      if (n) {
        toast.success(
          casoId ? "Agregados al caso" : "Correos traídos",
          `${n} correo${n === 1 ? "" : "s"} de tu buzón`,
        );
      }
      // «Ya estaba» tapaba tres situaciones distintas y solo una de ellas
      // significaba «no hay nada que hacer». Joe pidió agregar a un caso un
      // correo que llevaba rato suelto y el aviso lo despachó con un «ya está
      // en ArkTickets» — hoy ese caso ni siquiera llega aquí: se asigna.
      for (const o of r.omitidos) {
        if (o.motivo === "en_otro_caso") {
          toast.info(
            "Ese correo ya vive en otro caso",
            o.caso_folio
              ? `Está en el ${o.caso_folio}. Si de verdad va aquí, quítalo de allá primero.`
              : "Está en otro caso.",
          );
        } else if (o.mismo_caso) {
          toast.info("Ya lo tenías en este caso", "No hice nada: ya estaba aquí.");
        } else {
          toast.info(
            "Ese correo ya estaba",
            o.caso_folio ? `Alguien ya lo agregó al ${o.caso_folio}` : "Ya está en ArkTickets",
          );
        }
      }
      const perdidos = r.fallidos.filter((f) => f.motivo === "ya_no_esta").length;
      if (perdidos) {
        toast.info(
          `${perdidos} correo${perdidos === 1 ? "" : "s"} ya no está${perdidos === 1 ? "" : "n"} en tu buzón`,
          "Se movieron o se borraron desde tu correo.",
        );
      }
      setSeleccion(new Set());
      setAbierto(null);
      onJalado?.(r);
      if (!casoId && r.jalados.length) {
        const traidos = new Set(r.jalados.map((j) => j.uid));
        onJaladoSinCaso?.(
          r,
          refs.filter((x) => traidos.has(x.uid)),
        );
      }
      void cargar();
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : "Error inesperado";
      toast.error("No pudimos traer los correos", msg);
    } finally {
      setTrabajando(false);
    }
  }

  async function decidir(ruta: "ignorar" | "reconsiderar") {
    if (!refs.length) return;
    setTrabajando(true);
    try {
      await api.post(`/api/buzon/${ruta}`, {
        uidvalidity: lista?.uidvalidity ?? null,
        mensajes: refs,
      });
      toast.success(ruta === "ignorar" ? "Ignorados" : "De vuelta en la lista", "");
      setSeleccion(new Set());
      setAbierto(null);
      void cargar();
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : "Error inesperado";
      toast.error("No se pudo guardar", msg);
    } finally {
      setTrabajando(false);
    }
  }

  /** Los adjuntos que son archivos de verdad, no imágenes del cuerpo. */
  const archivos = useMemo(
    () => (cuerpo?.adjuntos ?? []).filter((a) => !a.es_inline),
    [cuerpo],
  );

  const posicion = useMemo(() => {
    const i = abierto ? mensajes.findIndex((m) => m.uid === abierto.uid) : -1;
    return { i, total: mensajes.length };
  }, [abierto, mensajes]);

  const apagado = estado != null && (!estado.configurado || !estado.habilitado);
  const n = objetivo.length;
  // Para el texto: no es lo mismo «2 seleccionados» que «este correo».
  const porSeleccion = seleccion.size > 0;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Revisar mi correo"
      description={
        lista?.buzon
          ? `${lista.buzon} · últimos ${lista.ventana_dias} días`
          : (estado?.buzon ?? "Tu correo empresarial")
      }
      size="xl"
      ayuda={
        <AyudaVista
          titulo="Revisar mi correo"
          {...ayudaRevisarCorreo({ enIgnorados: filtro === "ignorados", apagado })}
        />
      }
      footer={
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
          <p className="text-center text-[10px] leading-tight text-zinc-400 sm:text-[11px] sm:text-left">
            🔒 Solo lectura — nada se marca como leído ni se mueve de tu correo.
          </p>
          {/* En móvil: la acción PRINCIPAL ancha y el resto en una fila
              compacta. Apilar los cuatro se comía media pantalla y dejaba ver
              dos correos — reportado por Joe desde su teléfono. Y «Cerrar» ni
              aparece en móvil: para eso está la X de arriba, que ya mide 44 px.
              Desde `sm` todo vuelve a la línea. */}
          <div className="grid w-full grid-cols-2 items-stretch gap-2 sm:flex sm:w-auto
                          sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            {n ? (
              <span
                className="col-span-2 text-center text-xs text-zinc-400 sm:text-left"
                aria-live="polite"
              >
                {porSeleccion
                  ? `${n} seleccionado${n === 1 ? "" : "s"}`
                  : "Sobre el correo que estás viendo"}
              </span>
            ) : null}
            <button
              type="button"
              className="hidden btn-ghost text-zinc-300 sm:inline-flex"
              onClick={() => onOpenChange(false)}
            >
              Cerrar
            </button>
            {filtro === "ignorados" ? (
              <button
                type="button"
                className="btn col-span-2 min-h-[44px] w-full border border-ink-600 bg-ink-700
                           text-zinc-200 hover:bg-ink-600 disabled:opacity-50 sm:col-span-1
                           sm:min-h-0 sm:w-auto"
                disabled={!n || trabajando}
                onClick={() => void decidir("reconsiderar")}
              >
                Devolver a la lista
              </button>
            ) : (
              <>
                {/* PRIMERA FILA: los dos secundarios, uno en cada columna.
                    Con `col-span-2` suelto quedaban huecos —«Ignorar» solo en
                    su renglón y «Otro caso…» a media fila— y se veía roto.
                    Ícono + leyenda chiquita, la convención de la casa: el
                    ícono pelón no dice nada, pero con su palabra debajo ocupa
                    la mitad que un botón de texto. */}
                <button
                  type="button"
                  className="btn-ghost flex min-h-[44px] w-full flex-col items-center
                             justify-center gap-0.5 py-1 leading-none text-zinc-300
                             disabled:opacity-50 sm:min-h-0 sm:w-auto sm:flex-row sm:gap-1"
                  disabled={!n || trabajando}
                  onClick={() => void decidir("ignorar")}
                >
                  <IconoIgnorar className="h-5 w-5 sm:h-4 sm:w-4" />
                  <span className="text-[10px] sm:text-sm">Ignorar</span>
                </button>
                <button
                  type="button"
                  className="btn flex min-h-[44px] w-full flex-col items-center justify-center
                             gap-0.5 border border-ink-600 bg-ink-700 py-1 leading-none
                             text-zinc-200 hover:bg-ink-600 disabled:opacity-50 sm:min-h-0
                             sm:w-auto sm:flex-row sm:gap-1"
                  disabled={!n || trabajando}
                  onClick={() => {
                    if (casoComun) {
                      // NO se limpia `abierto`: el correo que estás viendo ES
                      // el objetivo de la acción cuando no hay selección.
                      // Limpiarlo dejaba el selector en «estos 0 correos» y el
                      // clic en un caso no hacía nada — lo reportó Joe.
                      setEligiendoCaso(true);
                    } else {
                      void jalar(null);
                    }
                  }}
                >
                  <IconoAgregar className="h-5 w-5 sm:hidden" />
                  <span className="text-[10px] sm:text-sm">
                    {casoComun ? "Otro caso…" : `Abrir caso${n > 1 ? ` (${n})` : ""}`}
                  </span>
                </button>

                {/* SEGUNDA FILA: la acción principal, ancha, y dice a dónde va
                    el correo. Si ya pertenece a un caso, el folio va en el
                    botón; si no, lo natural es mandarlo a un caso existente
                    que tú elijas — abrir uno nuevo ya está arriba. */}
                <button
                  type="button"
                  className="btn-primary col-span-2 min-h-[44px] w-full disabled:opacity-50
                             sm:col-span-1 sm:min-h-0 sm:w-auto"
                  disabled={!n || trabajando}
                  onClick={() => {
                    if (casoComun) void jalar(casoComun.id);
                    else {
                      // NO se limpia `abierto`: el correo que estás viendo ES
                      // el objetivo de la acción cuando no hay selección.
                      // Limpiarlo dejaba el selector en «estos 0 correos» y el
                      // clic en un caso no hacía nada — lo reportó Joe.
                      setEligiendoCaso(true);
                    }
                  }}
                >
                  {casoComun
                    ? `Agregar al ${folioCorto(casoComun.folio)}${n > 1 ? ` (${n})` : ""}`
                    : "Agregar a un caso…"}
                </button>
              </>
            )}
          </div>
        </div>
      }
    >
      {apagado ? (
        <div className="py-10 text-center">
          <p className="text-sm text-zinc-300">Todavía no conectas tu correo.</p>
          <p className="mx-auto mt-1 max-w-md text-xs text-zinc-400">
            ArkTickets puede leer los títulos de tu buzón para avisarte qué puede servir. Nunca
            marca como leído ni mueve nada.
          </p>
          <a href="/perfil" className="btn-primary mt-4 inline-block">
            Conectarlo en mi Perfil
          </a>
        </div>
      ) : (
        /* El cuerpo LLENA la ventana y las listas crecen con ella. Antes cada
           panel traía su propio `max-h` en `vh` —62vh, 44vh, un `calc(88vh-21rem)`—
           y como el modal mide 88vh menos encabezado, filtros y pie, sobraban
           ~250 px muertos abajo mientras las listas scrolleaban en una
           ventanita. Joe: «cortas todo, lo dejas chico, se desperdicia
           espacio». Con flex no hay aritmética que se desfase: lo que sobra, se
           reparte. */
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {/* Lo que explicaba el «?» con globito vive ahora en el botón
              «Ayuda» junto al título (`components/ayuda/vistas/revisarCorreo`). */}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {FILTROS.map((f) => {
              const activo = filtro === f.id;
              const c = lista?.conteos?.[f.conteo];
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => cambiarFiltro(f.id)}
                  className={
                    "min-h-[44px] rounded-full border px-3 py-1.5 text-xs transition sm:min-h-0 " +
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 " +
                    (activo
                      ? "border-accent-500 bg-accent-500 font-medium text-ink-950"
                      : "border-ink-600 text-zinc-300 hover:bg-ink-800")
                  }
                >
                  {f.etiqueta}
                  {c != null && c > 0 ? ` (${c})` : ""}
                </button>
              );
            })}
            {/* El aviso va a la vista, bajo la caja: buscar en el contenido
                tarda 9.75 s en este servidor de correo (medido; ver
                `services/buzon/cliente.py`), por eso solo remitente y asunto. */}
            <div className="ml-auto min-w-[12rem] flex-1 sm:max-w-xs">
              <label className="relative block">
                <IconoBuscar className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <input
                  className="input pl-8"
                  placeholder="Buscar remitente o título…"
                  value={q}
                  onChange={(e) => escribirBusqueda(e.target.value)}
                  aria-describedby="buzon-busqueda-ayuda"
                />
              </label>
              <p id="buzon-busqueda-ayuda" className="mt-1 text-[11px] text-zinc-500">
                Busca por remitente y título; el contenido no se busca (tardaría casi 10 segundos).
              </p>
            </div>
          </div>

          <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div
              className={
                "min-w-0 lg:flex lg:min-h-0 lg:flex-col " +
                (abierto ? "hidden lg:flex" : "block")
              }
            >
              {cargando ? (
                <SkeletonRows rows={6} cols={1} />
              ) : mensajes.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-zinc-300">
                    {filtro === "puede_servir"
                      ? "Nada nuevo que pueda servir."
                      : filtro === "ignorados"
                        ? "No has ignorado ningún correo."
                        : `No hay correos en los últimos ${dias} días.`}
                  </p>
                  {filtro === "puede_servir" && (
                    <button
                      type="button"
                      className="btn-ghost mt-2 text-accent-400"
                      onClick={() => cambiarFiltro("todo")}
                    >
                      Ver todo el buzón
                    </button>
                  )}
                  {filtro === "todo" && dias < 365 && (
                    <button
                      type="button"
                      className="btn-ghost mt-2 text-accent-400"
                      onClick={() => {
                        const d = dias < 90 ? 90 : 365;
                        setDias(d);
                        void cargar({ dias: d });
                      }}
                    >
                      Buscar más atrás
                    </button>
                  )}
                </div>
              ) : (
                /* En móvil el que scrollea es el cuerpo del modal; darle a la
                   lista su propio alto máximo anidaba dos áreas de scroll y
                   dejaba la lista en una ventanita. Desde `lg`, con dos
                   paneles, sí necesita el suyo. */
                <ul className="divide-y divide-ink-700 lg:scroll-sutil lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
                  {mensajes.map((m) => (
                    <FilaCorreoBuzon
                      key={m.uid}
                      mensaje={m}
                      seleccionado={seleccion.has(m.uid)}
                      activo={abierto?.uid === m.uid}
                      // Solo lo que YA vive en un caso queda fuera. Lo que está
                      // suelto en la bandeja o descartado SÍ se puede mandar a un
                      // caso desde aquí: es justo lo que no se podía hacer y
                      // dejaba correos en el limbo.
                      seleccionable={m.veredicto.tipo !== "ya_esta"}
                      onSeleccionar={() => alternar(m.uid)}
                      onAbrir={() => void abrirCorreo(m)}
                    />
                  ))}
                </ul>
              )}
            </div>

            <div
              className={
                "min-w-0 lg:flex lg:min-h-0 lg:flex-col " +
                (abierto || eligiendoCaso ? "block" : "hidden lg:flex")
              }
            >
              {eligiendoCaso ? (
                /* El selector vive AQUÍ, en el panel derecho, y no en otro modal
                   encima: `ui/Modal` scrollea su cuerpo, así que un modal
                   anidado con su propia lista se corta. Es el mismo motivo por
                   el que `ListaAsesores` existe aparte del picker. */
                <div className="rounded-lg border border-ink-700 bg-ink-900 p-3 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-200">
                        ¿A qué caso {n === 1 ? "va este correo" : `van estos ${n} correos`}?
                      </p>
                      {/* Cuál correo, con nombre y apellido: el selector tapa
                          el correo y sin esto se elige a ciegas. */}
                      {n === 1 && objetivo[0] ? (
                        <p className="break-words text-xs text-zinc-400">
                          {objetivo[0].asunto || "(sin asunto)"}
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() => setEligiendoCaso(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                  <input
                    className="input"
                    autoFocus
                    placeholder="Buscar por folio, cliente, TE o IMEI…"
                    value={qCaso}
                    onChange={(e) => setQCaso(e.target.value)}
                  />
                  <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] text-zinc-400">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={incluirCerrados}
                      onChange={(e) => setIncluirCerrados(e.target.checked)}
                    />
                    <ToggleTrack checked={incluirCerrados} />
                    Incluir casos cerrados
                  </label>
                  <ul className="scroll-sutil mt-2 max-h-[44vh] divide-y divide-ink-700 overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1">
                    {casos.length === 0 ? (
                      <li className="py-6 text-center text-sm text-zinc-500">
                        No hay casos con esa búsqueda.
                      </li>
                    ) : (
                      casos.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            disabled={trabajando}
                            onClick={() => {
                              setEligiendoCaso(false);
                              void jalar(c.id);
                            }}
                            className="w-full rounded px-2 py-2 text-left transition hover:bg-ink-800
                                       focus-visible:outline-none focus-visible:ring-2
                                       focus-visible:ring-accent-500 disabled:opacity-50"
                          >
                              {/* Con folio, cliente y estado NO alcanza: dos
                                casos del mismo cliente se ven idénticos y se
                                le puede pegar el correo al equivocado. Joe:
                                «no me dejó saber cuál era o de qué se trataba
                                ese caso, va a haber pedos con eso». Primero se
                                agregaron equipo, IMEI y fecha; seguía sin
                                bastar —«¿cómo sé de qué se tratan esos
                                tickets?», con dos ALVORE enfrente— así que el
                                renglón trae también DE QUÉ SE TRATA. */}
                            <div className="flex flex-wrap items-baseline gap-x-2">
                              <span className="font-mono text-xs text-accent-400">{c.folio}</span>
                              <span className="text-sm text-zinc-200">{c.cliente_nombre}</span>
                            </div>
                            <span className="block text-xs text-zinc-300">
                              {[c.marca, c.modelo].filter(Boolean).join(" ") || "sin equipo capturado"}
                              {c.imeis?.[0]?.imei ? ` · IMEI ${c.imeis[0].imei}` : ""}
                              {c.imeis && c.imeis.length > 1 ? ` +${c.imeis.length - 1}` : ""}
                            </span>
                            {c.descripcion && (
                              <span className="mt-0.5 block text-xs leading-snug text-zinc-400 line-clamp-2">
                                {c.descripcion}
                              </span>
                            )}
                            <span className="block text-[11px] text-zinc-500">
                              {c.estado === "Cerrado" ? "⚠ Cerrado" : c.estado}
                              {c.ticket_externo ? ` · ${c.ticket_externo}` : ""}
                              {c.asignado_a_nombre ? ` · ${c.asignado_a_nombre}` : ""}
                              {c.created_at
                                ? ` · ${new Date(c.created_at).toLocaleDateString("es-MX", {
                                    day: "numeric",
                                    month: "short",
                                  })}`
                                : ""}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              ) : !abierto ? (
                <p className="py-8 text-center text-sm text-zinc-500">
                  Elige un correo para verlo.
                </p>
              ) : (
                <div
                  className="rounded-lg border border-ink-700 bg-ink-900 p-3 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col"
                  onTouchStart={(e) => {
                    touchX.current = e.touches[0]?.clientX ?? null;
                  }}
                  onTouchEnd={(e) => {
                    if (touchX.current === null) return;
                    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
                    touchX.current = null;
                    // 60 px, el mismo umbral del visor de evidencias: por
                    // debajo de eso suele ser el pulgar al hacer scroll.
                    if (dx > 60) irA(-1);
                    else if (dx < -60) irA(1);
                  }}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="btn-ghost text-xs lg:hidden"
                      onClick={() => setAbierto(null)}
                    >
                      ‹ Volver a la lista
                    </button>
                    {/* La posición y las flechas. En móvil además se puede
                        deslizar; el texto lo dice para que el gesto se
                        descubra — un gesto que nadie sabe que existe no existe. */}
                    <div className="ml-auto flex items-center gap-1">
                      <span className="mr-1 text-[10px] text-zinc-500">
                        {posicion.i + 1} de {posicion.total}
                        <span className="lg:hidden"> · desliza</span>
                      </span>
                      <button
                        type="button"
                        aria-label="Correo anterior"
                        disabled={posicion.i <= 0}
                        onClick={() => irA(-1)}
                        className="flex h-9 w-9 items-center justify-center rounded text-zinc-400
                                   transition hover:bg-ink-800 hover:text-zinc-100
                                   focus-visible:outline-none focus-visible:ring-2
                                   focus-visible:ring-accent-500 disabled:opacity-30"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        aria-label="Correo siguiente"
                        disabled={posicion.i >= posicion.total - 1}
                        onClick={() => irA(1)}
                        className="flex h-9 w-9 items-center justify-center rounded text-zinc-400
                                   transition hover:bg-ink-800 hover:text-zinc-100
                                   focus-visible:outline-none focus-visible:ring-2
                                   focus-visible:ring-accent-500 disabled:opacity-30"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-zinc-200">{abierto.asunto}</p>
                  <p className="text-xs text-zinc-400">
                    {abierto.de_nombre ? `${abierto.de_nombre} · ` : ""}
                    {abierto.de}
                  </p>
                  <p className="text-[11px] text-zinc-500">
                    {abierto.fecha ? new Date(abierto.fecha).toLocaleString("es-MX") : ""}
                    {cuerpo?.tamano_kb ? ` · ${fmtBytes(cuerpo.tamano_kb * 1024)}` : ""}
                  </p>

                  {cargandoCuerpo ? (
                    <p className="mt-3 text-sm text-zinc-500">Trayendo el correo…</p>
                  ) : cuerpo?.existe ? (
                    <>
                      {/* Los archivos van ARRIBA del cuerpo, no debajo.
                          Debajo quedaban enterrados: había que scrollear todo
                          el correo para descubrir que traía un Excel — y en
                          una mesa de ayuda «qué archivo me mandaron» suele ser
                          lo primero que se busca, no lo último. */}
                      {/* Solo los archivos DE VERDAD. Las imágenes del cuerpo
                          (firmas, capturas pegadas) ya se ven arriba; listarlas
                          aquí sería ofrecer «descargar el logo de la firma». */}
                      {archivos.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {archivos.map((a) => (
                            <li key={`${a.es_enlace ? "u" : "m"}${a.indice}`}>
                              <button
                                type="button"
                                onClick={() => setVisor({ lista: archivos, i: archivos.indexOf(a) })}
                                className="flex w-full items-center gap-1.5 rounded px-1 py-1
                                           text-left text-xs text-amber-300 transition
                                           hover:bg-ink-800 focus-visible:outline-none
                                           focus-visible:ring-2 focus-visible:ring-accent-500"
                              >
                                <IconoAdjunto className="h-4 w-4 shrink-0" />
                                <span className="min-w-0 flex-1 truncate">{a.nombre}</span>
                                <span className="shrink-0 text-zinc-500">
                                  {a.es_enlace ? "enlace" : fmtBytes(a.tamano_kb * 1024)}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {/* Qué es «enlace», a la vista (era un tooltip). Solo se
                          baja al agregar el correo a un caso: `ingesta.jalar`
                          llama `_bajar_enlaces` únicamente con caso destino. */}
                      {archivos.some((a) => a.es_enlace) && (
                        <p className="mt-1 text-[11px] text-zinc-500">
                          «enlace»: el archivo vive en el servidor de quien te escribió y ArkTickets
                          lo trae por ti. Se guarda como evidencia cuando agregas el correo a un caso.
                        </p>
                      )}
                      {cuerpo.texto && cuerpo.html && (
                        <div className="mt-2 flex gap-1">
                          {(["texto", "html"] as const).map((v) => (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setVistaCuerpo(v)}
                              className={
                                "rounded border px-2 py-0.5 text-[10px] uppercase tracking-wide " +
                                (vistaCuerpo === v
                                  ? "border-accent-500 text-accent-300"
                                  : "border-ink-600 text-zinc-400 hover:text-zinc-200")
                              }
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* El cuerpo CRECE con el modal. La primera versión lo
                          calculaba a mano (`calc(88vh - 21rem)`, o sea el alto
                          del modal menos lo que ocupan título, filtros, datos
                          del remitente y pie): cualquier cambio en el
                          encabezado desfasaba la cuenta y volvía a sobrar
                          hueco. Ahora simplemente toma lo que quede. */}
                      {vistaCuerpo === "html" && cuerpo.html ? (
                        <div className="mt-2 h-[24rem] min-h-[16rem] lg:h-auto lg:min-h-0 lg:flex-1">
                          <EmailHtmlPreview
                            html={cuerpo.html}
                            cidMap={cuerpo.imagenes}
                            maxHeight="100%"
                            className="h-full"
                          />
                        </div>
                      ) : (
                        <pre className="scroll-sutil mt-2 max-h-[44vh] min-h-[8rem] overflow-auto
                                        whitespace-pre-wrap break-words rounded bg-ink-950 p-2
                                        text-xs text-zinc-300 lg:max-h-none lg:min-h-0 lg:flex-1">
                          {cuerpo.texto || "(sin texto)"}
                        </pre>
                      )}
                    </>
                  ) : (
                    <p className="mt-3 text-sm text-zinc-400">
                      Ese correo ya no está en tu buzón.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {visor && abierto && (
        <VisorArchivo
          targets={visor.lista.map((a) => ({
            nombre: a.nombre,
            mime: a.tipo || "application/octet-stream",
            // Dos orígenes, una sola experiencia: el que viene dentro del
            // correo y el que hay que ir a traer se abren igual, en el mismo
            // visor. Mandar al navegador a la URL externa dejaba una pestaña
            // en blanco en el celular — lo reportó Joe.
            url:
              `/api/buzon/mensajes/${abierto.uid}/` +
              `${a.es_enlace ? "enlace" : "adjunto"}/${a.indice}` +
              (abierto.message_id
                ? `?message_id=${encodeURIComponent(abierto.message_id)}`
                : ""),
          }))}
          inicial={visor.i}
          onClose={() => setVisor(null)}
        />
      )}
    </Modal>
  );
}
